import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { useActiveBusiness } from '@/hooks/useCurrentActiveBusiness'
import { orpc } from '@/lib/orpc/client'
import { zodResolver } from '@hookform/resolvers/zod'
import { BuildingIcon } from '@phosphor-icons/react'
import { updateBusinessSchema } from '@repo/shared/schemas/business'
import { useMutation } from '@tanstack/react-query'
import { createFileRoute, useBlocker } from '@tanstack/react-router'
import React from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { type z } from 'zod'

type BusinessSettingsForm = z.infer<typeof updateBusinessSchema>

export const Route = createFileRoute('/_dashboard/business-info/')({
  component: RouteComponent,
})

function RouteComponent() {
  const { activeBusiness } = useActiveBusiness()
  const updateBusinessMutation = useMutation(
    orpc.business.updateBusinessDetails.mutationOptions(),
  )

  const form = useForm<BusinessSettingsForm>({
    resolver: zodResolver(updateBusinessSchema),
    defaultValues: {
      ...activeBusiness,
    },
  })

  useBlocker({
    shouldBlockFn: () => {
      if (form.formState.isDirty) {
        const confirm = window.confirm(
          'You have unsaved changes. Are you sure you want to leave?',
        )
        if (confirm) {
          return true
        }
        return false
      }
      return false
    },
  })

  const onSubmit = async (data: BusinessSettingsForm) => {
    if (!activeBusiness) {
      toast.error('No active business found')
      return
    }

    try {
      await updateBusinessMutation.mutateAsync({
        id: activeBusiness.id,
        businessName: data.businessName,
        businessDescription: data.businessDescription,
        targetAudience: data.targetAudience,
        tone: data.tone,
        products: data.products,
        pricing: data.pricing,
      })

      toast.success('Business details updated successfully!')
    } catch (error) {
      console.error('Error updating business:', error)
      toast.error('Failed to update business details')
    }
  }

  // Update form when activeBusiness changes
  React.useEffect(() => {
    if (activeBusiness) {
      form.reset({
        ...activeBusiness,
      })
    }
  }, [activeBusiness, form])

  if (!activeBusiness) {
    return (
      <div className="container px-8 py-8 mx-auto max-w-7xl">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900">
            No Business Selected
          </h1>
          <p className="mt-2 text-gray-600">
            Please select a business to view settings.
          </p>
        </div>
      </div>
    )
  }
  return (
    <div className="px-8">
      <div className="py-8 mx-auto max-w-7xl">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <input
              type="hidden"
              {...form.register('id')}
              value={Number(activeBusiness.id)}
            />
            <div className="mb-8">
              <div className="flex gap-3 items-center mb-4 justify-between">
                <div className="flex items-center gap-3">
                  <BuildingIcon className="w-8 h-8" />
                  <h1 className="text-3xl font-bold text-gray-900">
                    Business Details
                  </h1>
                </div>

                <Button
                  type="submit"
                  disabled={updateBusinessMutation.isPending}
                >
                  {updateBusinessMutation.isPending
                    ? 'Saving...'
                    : 'Save Changes'}
                </Button>
              </div>
              <p className="mt-2 text-gray-600">
                Manage your business information and preferences. We'll use this
                information to create blog posts for your website.
              </p>
            </div>
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              {/* Business Information */}
              <div className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle>Business Information</CardTitle>
                    <CardDescription>
                      Core details about your business
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <FormField
                      control={form.control}
                      name="businessName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Business Name</FormLabel>
                          <FormControl>
                            <Input {...field} placeholder="Business Name" />
                          </FormControl>
                          <FormDescription>
                            Your business name as it should appear in blog posts
                            and content.
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="businessDescription"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Mission and Values</FormLabel>
                          <FormControl>
                            <Textarea {...field} rows={3} />
                          </FormControl>
                          <FormDescription>
                            Describe your business mission, values, and what
                            makes you unique. This helps the AI create authentic
                            content that reflects your brand.
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="targetAudience"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Target Audience</FormLabel>
                          <FormControl>
                            <Textarea {...field} rows={2} />
                          </FormControl>
                          <FormDescription>
                            Who are your ideal customers? Include demographics,
                            pain points, and interests to help the AI write
                            content that resonates with your audience.
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="tone"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Tone and Style</FormLabel>
                          <FormControl>
                            <Textarea {...field} rows={2} />
                          </FormControl>
                          <FormDescription>
                            Describe the writing style and tone you prefer
                            (e.g., professional, casual, technical, friendly).
                            This ensures all generated content matches your
                            brand voice.
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="other"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Other</FormLabel>
                          <FormControl>
                            <Textarea
                              {...field}
                              rows={2}
                              value={field.value || ''}
                            />
                          </FormControl>
                          <FormDescription>
                            Other information about your business. This could be
                            anything you think the AI should know when writing
                            blog posts.
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </CardContent>
                </Card>
              </div>

              {/* Pricing and Recent Posts */}
              <div className="space-y-4">
                {/* Pricing */}
                <Card>
                  <CardHeader>
                    <CardTitle>Pricing</CardTitle>
                    <CardDescription>
                      Your pricing structure and packages
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <FormField
                      control={form.control}
                      name="pricing"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Pricing Tiers</FormLabel>
                          <FormControl>
                            <Textarea
                              {...field}
                              rows={4}
                              placeholder="Starter: $99/month - Basic features&#10;Professional: $299/month - Advanced features + support&#10;Enterprise: Custom pricing - Full customization + dedicated team"
                            />
                          </FormControl>
                          <FormDescription>
                            Outline your pricing structure and packages. This
                            information helps create content that addresses
                            pricing concerns and highlights value propositions.
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </CardContent>
                </Card>

                {/* Products/Services */}
                <Card>
                  <CardHeader>
                    <CardTitle>Products & Services</CardTitle>
                    <CardDescription>What your business offers</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <FormField
                      control={form.control}
                      name="products"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Primary Products/Services</FormLabel>
                          <FormControl>
                            <Textarea
                              {...field}
                              onChange={(e) => {
                                field.onChange(e.target.value.split('\n'))
                              }}
                              value={field.value?.join('\n') || ''}
                              rows={5}
                              placeholder="• Custom Software Development&#10;• Cloud Infrastructure Solutions&#10;• Digital Transformation Consulting&#10;• Technical Support & Maintenance&#10;• Training & Workshops"
                            />
                          </FormControl>
                          <FormDescription>
                            List your main products or services, one per line.
                            This helps the AI create relevant content that
                            showcases your offerings.
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </CardContent>
                </Card>
              </div>
            </div>
          </form>
        </Form>
      </div>
    </div>
  )
}
